import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { DeepLinkFacade } from '@core/deep-link/store/facade/deep-link.facade';
import { ZendeskFacade } from '@core/sso/facades/zendesk.facade';
import { KaseyaStoreQueryParams } from '@core/sso/models/kaseya-store-query-params.interface';
import { AuthorizationRequest } from '@core/sso/models/open-id-connect/authorization-request.interface';
import { PostLoginAction } from '@core/sso/models/post-login-action';
import { PostLoginWorkflow } from '@core/sso/models/post-login-workflow.enum';
import { ZendeskLoginRequest } from '@core/sso/models/zendesk/zendesk-login-request.interface';
import { KaseyaCommunityFacade } from '@core/sso/services/kaseya-community/kaseya-community.facade';
import { OpenIdConnectService } from '@core/sso/services/open-id-connect/open-id-connect.service';
import { isNotNull } from '@kaseya-utils';
import { BehaviorSubject, filter, switchMap } from 'rxjs';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@core/permissions/models/permission.enum';
import { ToastService } from '@core/toast/toast.service';

@Injectable({ providedIn: 'root' })
export class PostLoginActionHandlerService {
  private readonly router = inject(Router);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly kaseyaCommunityFacade = inject(KaseyaCommunityFacade);
  private readonly openIdConnectService = inject(OpenIdConnectService);
  private readonly deepLinkFacade = inject(DeepLinkFacade);
  private readonly zendeskFacade = inject(ZendeskFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly toastService = inject(ToastService);
  private readonly hasGainsightPermission = this.permissionsFacade.hasPermission(Permission.Gainsight);
  private readonly hasOpenIdConnectPermission = this.permissionsFacade.hasPermission(Permission.OpenIdConnectUser);
  private readonly hasZendeskPermission = this.permissionsFacade.hasPermission(Permission.Zendesk);

  private postLoginActionSubject = new BehaviorSubject<PostLoginAction | null>(null);

  constructor() {
    this.authorizationService.onLogin
      .pipe(
        switchMap(() => this.postLoginActionSubject),
        filter(postLoginAction => isNotNull(postLoginAction))
      )
      .subscribe(() => this.applyPostLoginAction());
  }

  setSsoAction(snapshot: ActivatedRouteSnapshot | null): void {
    const workflow = snapshot?.data?.workflow as PostLoginWorkflow;

    if (!snapshot || !workflow) {
      this.postLoginActionSubject.next(PostLoginAction.Default);
      return;
    }

    const payload = { ...snapshot.params, ...snapshot.queryParams } as AuthorizationRequest | ZendeskLoginRequest;
    const postLoginAction = new PostLoginAction(workflow, payload);

    this.postLoginActionSubject.next(postLoginAction);
  }

  private applyPostLoginAction(): void {
    if (!this.authorizationService.isAuthorized()) {
      return;
    }

    const workflow = this.postLoginActionSubject?.value?.workflow;
    const payload = this.postLoginActionSubject?.value?.payload;

    switch (workflow) {
      case PostLoginWorkflow.Default:
        this.handleDefaultPostLoginRedirect();
        break;
      case PostLoginWorkflow.OpenIdConnect:
        this.handleOpenIdConnectPostLoginAction(payload as AuthorizationRequest);
        break;
      case PostLoginWorkflow.KaseyaCommunity:
        this.handleKaseyaCommunityPostLoginAction();
        break;
      case PostLoginWorkflow.KaseyaZendeskLogin:
        this.handleZendeskPostLoginAction();
        break;
      case PostLoginWorkflow.KaseyaStore:
        this.goToKaseyaStoreLoadingPage(payload as KaseyaStoreQueryParams);
        break;
    }
  }

  private handleDefaultPostLoginRedirect(): void {
    const deepLink = this.deepLinkFacade.deepLink();

    if (!deepLink) {
      this.goHome();
      return;
    }

    this.authorizationService.completeLoginAfterNavigation(this.router.navigateByUrl(deepLink));
    this.deepLinkFacade.clearState();
  }

  private goHome(): void {
    this.authorizationService.completeLoginAfterNavigation(this.router.navigate(['home']));
  }

  private goToKaseyaStoreLoadingPage(payload: KaseyaStoreQueryParams): void {
    this.router.navigate(['store'], { queryParams: { targetPage: payload.targetPage } });
  }

  private handleKaseyaCommunityPostLoginAction(): void {
    if (!this.hasGainsightPermission()) {
      this.toastService.showErrorToast('sso.messages.gainsight-permission-denied');
      this.goHome();
      return;
    }

    this.kaseyaCommunityFacade.initSso();
  }

  private handleOpenIdConnectPostLoginAction(payload: AuthorizationRequest): void {
    if (!this.hasOpenIdConnectPermission()) {
      this.router.navigate(['product-access-denied']);
      return;
    }

    this.openIdConnectService.initAuthorizationCodeFlow(payload);
  }

  private handleZendeskPostLoginAction(): void {
    if (!this.hasZendeskPermission()) {
      this.toastService.showErrorToast('sso.messages.zendesk-permission-denied');
      this.goHome();
      return;
    }

    const request = this.postLoginActionSubject.value?.payload as ZendeskLoginRequest;
    this.zendeskFacade.initZendeskFlow(request);
  }
}
